// Pure TS Layer: React 非依存
// mqtt.js + SigV4 で AWS IoT Core に WSS 接続するクライアント

import mqtt, { type IClientOptions, type MqttClient } from 'mqtt';
import { ensureIotPolicyAttached, getIotCredentials } from '@/hooks/auth/cognitoCredentials';
import type { IotAuthConfig } from '@/hooks/auth/cognitoConfig';
import { signWebSocketUrl } from './signWebSocketUrl';

export type IncomingMessage = {
  readonly topic: string;
  readonly payload: string;
  readonly receivedAt: Date;
};

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

export type MqttClientEvents = {
  onState: (state: ConnectionState, detail?: string) => void;
  onMessage: (msg: IncomingMessage) => void;
};

export type MqttConnectionHandle = {
  readonly subscribe: (topicFilter: string) => Promise<void>;
  readonly unsubscribe: (topicFilter: string) => Promise<void>;
  readonly disconnect: () => Promise<void>;
};

function makeClientId(): string {
  // IoT Policy 側で client/hfi-console-* に絞っているため prefix を強制
  return `hfi-console-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

/**
 * AWS IoT Core に WSS で接続する。
 * 接続中の状態遷移とメッセージ受信を events 経由でコールバック。
 *
 * 再接続時は transformWsUrl で SigV4 を再署名する。
 */
export async function connectMqtt(
  config: IotAuthConfig,
  events: MqttClientEvents,
): Promise<MqttConnectionHandle> {
  events.onState('connecting', 'Fetching IAM credentials');

  // 1. IAM 資格を取得し、IoT policy を attach
  await ensureIotPolicyAttached(config);
  const { credentials } = await getIotCredentials(config);

  events.onState('connecting', 'Signing WebSocket URL');
  const initialUrl = await signWebSocketUrl({
    region: config.region,
    iotEndpoint: config.iotEndpoint,
    credentials,
  });

  // 再接続時に最新の署名済 URL を返すためのキャッシュ
  // (mqtt.js の transformWsUrl は同期 API のため、定期的に背景で再署名する)
  let currentSignedUrl = initialUrl;
  const RESIGN_INTERVAL_MS = 4 * 60 * 1000; // 4 分ごと (有効期限 5 分 - バッファ 1 分)
  const resignTimer = setInterval(() => {
    void (async () => {
      try {
        const { credentials: refreshed } = await getIotCredentials(config);
        currentSignedUrl = await signWebSocketUrl({
          region: config.region,
          iotEndpoint: config.iotEndpoint,
          credentials: refreshed,
        });
      } catch {
        /* swallow — 次の interval で再試行 */
      }
    })();
  }, RESIGN_INTERVAL_MS);

  const clientId = makeClientId();
  const options: IClientOptions = {
    clientId,
    protocolVersion: 4, // MQTT 3.1.1 (AWS IoT Core で安定動作)
    keepalive: 60,
    reconnectPeriod: 5000,
    connectTimeout: 30 * 1000,
    clean: true,
    transformWsUrl: () => currentSignedUrl,
  };

  events.onState('connecting', `Connecting as ${clientId}`);
  const client = mqtt.connect(initialUrl, options);

  client.on('connect', () => events.onState('connected'));
  client.on('reconnect', () => events.onState('reconnecting'));
  client.on('close', () => events.onState('disconnected'));
  client.on('offline', () => events.onState('disconnected', 'offline'));
  client.on('error', (err) => events.onState('error', err.message));
  client.on('message', (topic, payload) => {
    events.onMessage({
      topic,
      payload: payload.toString(),
      receivedAt: new Date(),
    });
  });

  return {
    subscribe: (topicFilter) =>
      new Promise<void>((resolve, reject) => {
        client.subscribe(topicFilter, { qos: 0 }, (err) => {
          if (err) reject(err);
          else resolve();
        });
      }),
    unsubscribe: (topicFilter) =>
      new Promise<void>((resolve, reject) => {
        client.unsubscribe(topicFilter, (err) => {
          if (err) reject(err);
          else resolve();
        });
      }),
    disconnect: () =>
      new Promise<void>((resolve) => {
        clearInterval(resignTimer);
        client.end(false, {}, () => resolve());
      }),
  };
}

export type { MqttClient };
